// src/tabs/AlbumsTab.jsx
import React, { useMemo, useRef, useState, useEffect } from "react";
import PageHeader from "../components/PageHeader";
import Button from "../components/Button";
import { resolveCoverUrl, coverOnError } from "../utils/covers";
import { albumKey, STATIC_ALBUMS } from "../data/albums.static";
import {
  playSongByTitle,
  playAlbumBestEffort,
  getPlayer,
  fetchAccessToken,
} from "../utils/spotify";

/* ------------------------------ helpers ------------------------------ */

const byYear = (a, b) => (a.year || 0) - (b.year || 0);
const decadeLabel = (y) => {
  if (typeof y === "string") return "Collections"; // For Christmas Vols
  return typeof y === "number" ? `${Math.floor(y / 10) * 10}s` : "Unknown";
};

const FIXED_DECADES = [
  "1960s",
  "1970s",
  "1980s",
  "1990s",
  "2000s",
  "2010s",
  "2020s",
];

// Helper to transform the flat Christmas list into "Album Volumes"
function transformChristmasData(items) {
  const chunkSize = 25;
  const volumes = [];
  const COVERS = [
    "https://images.unsplash.com/photo-1543589077-47d81606c1bf?auto=format&fit=crop&w=400&q=80",
    "https://images.unsplash.com/photo-1482517967863-00e15c9b44be?auto=format&fit=crop&w=400&q=80",
    "https://images.unsplash.com/photo-1514361892635-6b07e31e75f9?auto=format&fit=crop&w=400&q=80",
    "https://images.unsplash.com/photo-1576919228236-a0174b272935?auto=format&fit=crop&w=400&q=80",
  ];

  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    const volNum = i / chunkSize + 1;
    volumes.push({
      id: `xmas-vol-${volNum}`,
      title: `Christmas Hits Vol. ${volNum}`,
      year: "Holiday Collection",
      type: "playlist",
      cover: COVERS[volNum - 1] || COVERS[0],
      isChristmas: true,
      songs: chunk.map((s) => ({
        id: `xmas-${s.rank}`,
        title: s.title,
        artist: s.artist,
        track: s.rank,
        releaseTitle: `Christmas Hits Vol. ${volNum}`,
        recorded_year: null,
      })),
    });
  }
  return volumes;
}

/* ------------------------- Spotify helpers (local) -------------------- */

async function searchOneTrack(accessToken, { title, artist = "Bob Dylan" }) {
  const q = `track:${title} artist:${artist}`;
  const url = `https://api.spotify.com/v1/search?q=${encodeURIComponent(
    q
  )}&type=track&limit=1`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error("search_failed");
  const json = await res.json();
  return json?.tracks?.items?.[0] || null;
}

async function addToSpotifyQueue(accessToken, uri) {
  const url = `https://api.spotify.com/v1/me/player/queue?uri=${encodeURIComponent(
    uri
  )}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) {
    const t = await res.text().catch(() => "");
    throw new Error(`queue_failed_${res.status} ${t}`);
  }
}

/* ------------------------- Mini Now Playing bar ------------------------ */

function NowPlayingBar() {
  const [player, setPlayer] = useState(null);
  const [state, setState] = useState(null);
  const [volume, setVolume] = useState(0.8);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getPlayer().catch(() => null);
        if (!mounted || !p) return;
        setPlayer(p);
        try {
          await p.setVolume(volume);
        } catch {}

        const onState = (s) => {
          setState(s);
          setVisible(
            Boolean(s && s.track_window && s.track_window.current_track)
          );
        };
        p.addListener("player_state_changed", onState);
        try {
          const s = await p.getCurrentState();
          if (s) onState(s);
        } catch {}
        return () => p.removeListener("player_state_changed", onState);
      } catch {}
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!visible) return null;

  const current = state?.track_window?.current_track || null;
  const paused = state?.paused ?? true;
  const art = current?.album?.images?.[0]?.url || "";
  const title = current?.name || "—";
  const artists = (current?.artists || []).map((a) => a.name).join(", ");

  const handleToggle = async () => {
    try {
      await player?.togglePlay();
    } catch (e) {
      console.error(e);
    }
  };
  const handlePrev = async () => {
    try {
      await player?.previousTrack();
    } catch (e) {
      console.error(e);
    }
  };
  const handleNext = async () => {
    try {
      await player?.nextTrack();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div
      role="region"
      aria-label="Now playing"
      style={{
        position: "fixed",
        top: "clamp(72px, 8vh, 120px)",
        right: 16,
        zIndex: 60,
        background: "white",
        border: "1px solid var(--line)",
        borderRadius: 16,
        boxShadow: "0 12px 30px rgba(0,0,0,.25)",
        padding: 10,
        display: "flex",
        alignItems: "center",
        gap: 10,
        minWidth: 280,
        maxWidth: 420,
      }}
    >
      <img
        src={art}
        alt=""
        width={44}
        height={44}
        style={{
          borderRadius: 8,
          objectFit: "cover",
          border: "1px solid var(--line)",
          background: "var(--canvas)",
          flexShrink: 0,
        }}
        onError={(e) => (e.currentTarget.style.visibility = "hidden")}
      />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          className="sm"
          style={{
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
        </div>
        <div
          className="xs muted"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {artists || "—"}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button className="btn icon" onClick={handlePrev}>
          ⏮
        </button>
        <button
          className="btn icon"
          onClick={handleToggle}
          style={{ background: "var(--brand10)" }}
        >
          {paused ? "▶️" : "⏸"}
        </button>
        <button className="btn icon" onClick={handleNext}>
          ⏭
        </button>
      </div>
    </div>
  );
}

/* ------------------------------ Queue UI ------------------------------ */

function QueueDrawer({
  items,
  onRemove,
  onClear,
  onPlayNow,
  onSend,
  open,
  setOpen,
}) {
  if (!open) return null;
  return (
    <>
      <div
        onClick={() => setOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 70,
        }}
      />
      <div
        style={{
          position: "fixed",
          left: 16,
          right: 16,
          top: "clamp(84px, 10vh, 140px)",
          margin: "0 auto",
          width: "min(520px, 96vw)",
          zIndex: 71,
          background: "white",
          borderRadius: 16,
          boxShadow: "0 16px 48px rgba(0,0,0,.35)",
          border: "1px solid var(--line)",
          display: "grid",
          gridTemplateRows: "auto 1fr auto",
          maxHeight: "70vh",
          overflow: "hidden",
        }}
      >
        <div
          className="sm"
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: 12,
            borderBottom: "1px solid var(--line)",
          }}
        >
          <div style={{ fontWeight: 700 }}>Up Next ({items.length})</div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              className="secondary"
              onClick={onClear}
              disabled={!items.length}
            >
              Clear
            </Button>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          {items.length === 0 ? (
            <div className="xs muted" style={{ padding: 14 }}>
              Nothing queued yet.
            </div>
          ) : (
            items.map((t, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: 12,
                  borderBottom: "1px solid var(--line)",
                }}
              >
                <div>
                  <div className="sm" style={{ fontWeight: 600 }}>
                    {t.title}
                  </div>
                  <div className="xs muted">{t.artist || "Bob Dylan"}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={() => onPlayNow(t.title, t.artist)}>
                    Play
                  </Button>
                  <Button className="secondary" onClick={() => onRemove(i)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
        <div style={{ padding: 12, borderTop: "1px solid var(--line)" }}>
          <Button
            onClick={onSend}
            disabled={!items.length}
            style={{ width: "100%" }}
          >
            Send to Spotify Queue
          </Button>
        </div>
      </div>
    </>
  );
}

/* ---------------------------- album overlay --------------------------- */

function AlbumOverlay({ album, onClose, onQueue }) {
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (toast) setTimeout(() => setToast(null), 2800);
  }, [toast]);

  // --- UPDATED: Smart Play Function with "Wake Up" logic ---
  const tryPlay = async (title, artist) => {
    try {
      setBusy(true);
      setToast("Connecting to Spotify...");

      // 1. Get a fresh token
      const { access_token } = await fetchAccessToken();

      // 2. Find the song URI (Standard Spotify Search)
      const q = `track:${title} artist:${artist || "Bob Dylan"}`;
      const searchRes = await fetch(
        `https://api.spotify.com/v1/search?q=${encodeURIComponent(q)}&type=track&limit=1`,
        { headers: { Authorization: `Bearer ${access_token}` } }
      );
      const searchJson = await searchRes.json();
      const trackUri = searchJson?.tracks?.items?.[0]?.uri;

      if (!trackUri) throw new Error("Song not found on Spotify");

      // 3. Find an Active Device
      const devicesRes = await fetch("https://api.spotify.com/v1/me/player/devices", {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      const devicesData = await devicesRes.json();
      const activeDevice = devicesData.devices.find((d) => d.is_active);
      const targetDevice = activeDevice || devicesData.devices[0];

      if (!targetDevice) {
        setToast("Please open Spotify on your device first!");
        setBusy(false);
        return;
      }

      // 4. Wake Up Device (Transfer Playback) if needed
      if (!activeDevice) {
        await fetch("https://api.spotify.com/v1/me/player", {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ device_ids: [targetDevice.id], play: false }),
        });
        // Wait 500ms for the device to wake up
        await new Promise((r) => setTimeout(r, 500));
      }

      // 5. Play the Song
      const playRes = await fetch(
        `https://api.spotify.com/v1/me/player/play?device_id=${targetDevice.id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ uris: [trackUri] }),
        }
      );

      if (!playRes.ok) {
        throw new Error("API refused play command");
      }

      setToast(`Playing “${title}”…`);
    } catch (e) {
      console.error(e);
      setToast("Playback failed (Check Spotify App)");
    } finally {
      setBusy(false);
    }
  };

  if (!album) return null;

  const staticData = STATIC_ALBUMS[albumKey(album.title, album.year)];
  let trackList = [];
  if (album.songs && album.songs.length > 0) {
    trackList = album.songs.map((s) => ({
      title: s.title,
      artist: s.artist || "Bob Dylan",
      dur: "—",
    }));
  } else if (staticData && staticData.tracks) {
    trackList = staticData.tracks.map(([t, dur]) => ({
      title: t,
      artist: "Bob Dylan",
      dur,
    }));
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 50,
        }}
      />
      <div
        style={{
          position: "fixed",
          inset: "6% 10%",
          background: "white",
          borderRadius: 20,
          zIndex: 51,
          padding: 0,
          display: "grid",
          gridTemplateRows: "auto 1fr",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: 16,
            borderBottom: "1px solid var(--line)",
            display: "flex",
            gap: 16,
            alignItems: "center",
          }}
        >
          <img
            src={resolveCoverUrl(album.cover)}
            alt=""
            style={{
              width: 64,
              height: 64,
              borderRadius: 8,
              objectFit: "cover",
            }}
            onError={coverOnError}
          />
          <div>
            <div className="h2">{album.title}</div>
            <div className="xs muted">{album.year}</div>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <Button className="secondary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
        <div style={{ padding: 16, overflow: "auto" }}>
          {trackList.length === 0 ? (
            <div className="muted">No tracklisting available.</div>
          ) : (
            <div className="grid">
              {trackList.map((t, i) => (
                <div
                  key={i}
                  className="card"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <span className="xs muted" style={{ width: 20 }}>
                      #{i + 1}
                    </span>
                    <span style={{ fontWeight: 500 }}>{t.title}</span>
                    {t.artist !== "Bob Dylan" && (
                      <span className="xs muted">by {t.artist}</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button
                      onClick={() => tryPlay(t.title, t.artist)}
                      disabled={busy}
                    >
                      ▶
                    </Button>
                    <Button className="secondary" onClick={() => onQueue(t)}>
                      ＋ Queue
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {toast && (
          <div
            style={{
              position: "absolute",
              bottom: 20,
              left: 20,
              background: "#333",
              color: "white",
              padding: "8px 16px",
              borderRadius: 8,
              zIndex: 100,
            }}
          >
            {toast}
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------- main tab ------------------------------ */

export default function AlbumsTab({ albums = [], onOpenAlbum }) {
  const [collection, setCollection] = useState("dylan");
  const [christmasAlbums, setChristmasAlbums] = useState([]);
  const [q, setQ] = useState("");
  const [openAlbum, setOpenAlbum] = useState(null);
  const [queueOpen, setQueueOpen] = useState(false);
  const [queue, setQueue] = useState([]);

  useEffect(() => {
    if (collection === "christmas" && christmasAlbums.length === 0) {
      fetch("/data/christmas_songs_100.json")
        .then((res) => res.json())
        .then((data) => {
          const vols = transformChristmasData(data.items || []);
          setChristmasAlbums(vols);
        })
        .catch((err) => console.error("Failed to load xmas", err));
    }
  }, [collection, christmasAlbums]);

  const activeList = collection === "dylan" ? albums : christmasAlbums;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let list = [...activeList];
    if (collection === "dylan") list.sort(byYear);
    if (!t) return list;
    return list.filter((a) => a.title.toLowerCase().includes(t));
  }, [q, activeList, collection]);

  const grouped = useMemo(() => {
    if (collection === "christmas") return { "Holiday Collection": filtered };
    const map = {};
    FIXED_DECADES.forEach((d) => (map[d] = []));
    filtered.forEach((a) => {
      const d = decadeLabel(a.year);
      if (!map[d]) map[d] = [];
      map[d].push(a);
    });
    return map;
  }, [filtered, collection]);

  const addToQueue = (item) => setQueue((prev) => [...prev, item]);
  const removeFromQueue = (idx) =>
    setQueue((prev) => prev.filter((_, i) => i !== idx));
  const clearQueue = () => setQueue([]);

  const playNow = async (title, artist) => {
    try {
      await playSongByTitle({ title, artist: artist || "Bob Dylan" });
    } catch (e) {
      console.error(e);
    }
  };

  const sendAllToSpotifyQueue = async () => {
    try {
      await getPlayer().catch(() => {});
      const { access_token } = await fetchAccessToken();
      for (const item of queue) {
        const t = await searchOneTrack(access_token, {
          title: item.title,
          artist: item.artist,
        });
        if (t?.uri) await addToSpotifyQueue(access_token, t.uri);
      }
    } catch (e) {
      alert("Error sending queue");
    }
  };

  const getToggleStyle = (id) => ({
    background:
      collection === id
        ? id === "christmas"
          ? "#d1fae5"
          : "#e8efff"
        : "transparent",
    color:
      collection === id
        ? id === "christmas"
          ? "#065f46"
          : "#1f4dd8"
        : "var(--muted)",
    borderColor:
      collection === id
        ? id === "christmas"
          ? "#a7f3d0"
          : "#c6d4ff"
        : "transparent",
  });

  return (
    <>
      <PageHeader
        title={
          collection === "dylan" ? "Albums (Chronological)" : "Family Christmas"
        }
        description={
          collection === "dylan"
            ? "Open any album to view its songs."
            : "Top 100 Holiday Classics, organized into volumes."
        }
      >
        <div className="toolbar" style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 8, marginRight: "auto" }}>
            <Button
              className="btn"
              style={getToggleStyle("dylan")}
              onClick={() => setCollection("dylan")}
            >
              Bob Dylan
            </Button>
            <Button
              className="btn"
              style={getToggleStyle("christmas")}
              onClick={() => setCollection("christmas")}
            >
              Family Christmas
            </Button>
          </div>
          <label>
            <input
              className="input"
              type="search"
              placeholder="Search..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ minWidth: 200 }}
            />
          </label>
        </div>
      </PageHeader>

      <button
        onClick={() => setQueueOpen(true)}
        style={{
          position: "fixed",
          top: "clamp(72px, 8vh, 120px)",
          right: 456,
          zIndex: 61,
          background: "white",
          border: "1px solid var(--line)",
          borderRadius: 999,
          padding: "8px 12px",
          boxShadow: "0 8px 24px rgba(0,0,0,.2)",
          cursor: "pointer",
        }}
      >
        Up Next ({queue.length})
      </button>

      {Object.entries(grouped).map(([label, list]) => {
        if (!list || list.length === 0) return null;
        return (
          <section key={label} className="card lg" style={{ marginBottom: 24 }}>
            <div className="h2" style={{ marginBottom: 16 }}>
              <span className="pill">{label}</span>
            </div>
            <div className="grid grid-cols">
              {list.map((a) => (
                <button
                  key={a.id || a.title}
                  className="card"
                  onClick={() => setOpenAlbum(a)}
                  style={{
                    textAlign: "left",
                    padding: 14,
                    background:
                      "linear-gradient(135deg, var(--brand10), #ffffff)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", gap: 12 }}>
                    <img
                      src={resolveCoverUrl(a.cover)}
                      alt=""
                      width={72}
                      height={72}
                      style={{
                        borderRadius: 12,
                        border: "1px solid var(--line)",
                        objectFit: "cover",
                        background: "#eee",
                      }}
                      onError={coverOnError}
                    />
                    <div>
                      <div
                        className="h2"
                        style={{ fontSize: 16, lineHeight: "1.3" }}
                      >
                        {a.title}
                      </div>
                      <div className="xs muted">
                        {a.year} ·{" "}
                        {(a.songs || []).length > 0
                          ? `${a.songs.length} songs`
                          : a.type}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        );
      })}

      {openAlbum && (
        <AlbumOverlay
          album={openAlbum}
          onClose={() => setOpenAlbum(null)}
          onQueue={addToQueue}
        />
      )}
      <QueueDrawer
        items={queue}
        open={queueOpen}
        setOpen={setQueueOpen}
        onRemove={removeFromQueue}
        onClear={clearQueue}
        onPlayNow={playNow}
        onSend={sendAllToSpotifyQueue}
      />
      <NowPlayingBar />
    </>
  );
}
